import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  FactCheck as FactCheckIcon,
  FilterList as FilterListIcon,
  Save as SaveIcon,
  Timeline as TimelineIcon,
  VerifiedUser as VerifiedUserIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import {
  CustomAutoComplete,
  CustomButton,
  DialogBody,
  DialogContainer,
  DialogTitle,
} from '../../../../common/components';
import apiClient from '../../../../hooks/apiClient';
import { useBulkUpdateProgress } from '../../../../hooks/useProjectProgressTracking';
import { useBulkUpdateQuality } from '../../../../hooks/useProjectQualityTracking';

const SHEET_CONFIG = {
  progress: {
    sheetName: 'AVANCE FISICO',
  },
  quality: {
    sheetName: 'AVANCE DE CALIDAD',
  },
};

const DEFAULT_ADVANCE_COLUMN_INDEX = 6;
const CHANGE_TOLERANCE = 0.05;
const PROGRESS_ABSOLUTE_TOLERANCE = 0.005;

const normalizeText = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const compactKey = (value) => normalizeText(value).replace(/[^A-Z0-9]/g, '');

const getCellText = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

const toDisplayPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0.0%';
  return `${number.toFixed(1)}%`;
};

const clampPercent = (value) => Math.min(100, Math.max(0, value));

const roundToTwo = (value) => Math.round((Number(value) || 0) * 100) / 100;

const getArgentinaDate = () => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Argentina/Mendoza',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

const parsePercentValue = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return clampPercent(value <= 1 ? value * 100 : value);
  }

  const rawText = String(value).trim();
  if (!rawText) return null;

  const numericText = rawText.replace('%', '').replace(/[^\d,.-]/g, '');
  const hasComma = numericText.includes(',');
  const hasDot = numericText.includes('.');
  const cleaned = hasComma && hasDot
    ? numericText.replace(/\./g, '').replace(',', '.')
    : numericText.replace(',', '.');

  if (!cleaned || cleaned === '-' || cleaned === '.') return null;

  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) return null;

  return clampPercent(rawText.includes('%') || parsed > 1 ? parsed : parsed * 100);
};

const createProjectLookup = (projects = []) => {
  const exact = new Map();
  const compact = new Map();

  projects.forEach((project) => {
    if (!project?.uuid || !project?.name) return;

    exact.set(normalizeText(project.name), project);
    compact.set(compactKey(project.name), project);
  });

  return {
    find: (rawName) => {
      const exactKey = normalizeText(rawName);
      const compactName = compactKey(rawName);

      if (!exactKey || !compactName) return null;

      if (exact.has(exactKey)) return exact.get(exactKey);
      return compact.has(compactName) ? compact.get(compactName) : null;
    },
  };
};

const shouldIgnoreProjectCell = (value) => {
  const text = getCellText(value);
  if (!text) return true;

  const normalized = normalizeText(text);
  if (['FECHADECIERRE', 'FECHA DE CIERRE', 'NOMBREDETAREA', 'NOMBRE DE TAREA'].includes(normalized)) {
    return true;
  }

  return !/[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(text);
};

const shouldIgnoreTaskName = (value) => {
  const normalized = normalizeText(value);
  return (
    !normalized ||
    normalized === 'NOMBREDETAREA' ||
    normalized === 'NOMBRE DE TAREA' ||
    normalized === 'TAREA' ||
    normalized === 'TASK'
  );
};

const findSheet = (workbook, targetName) => {
  const target = normalizeText(targetName);
  const sheetName = workbook.SheetNames.find((name) => normalizeText(name) === target);
  return sheetName ? workbook.Sheets[sheetName] : null;
};

const detectAdvanceColumns = (rows) => {
  const candidates = new Set([DEFAULT_ADVANCE_COLUMN_INDEX]);

  rows.slice(0, 12).forEach((row) => {
    row.forEach((cell, index) => {
      if (normalizeText(cell).includes('AVANCE')) {
        candidates.add(index);
      }
    });
  });

  return Array.from(candidates);
};

const parseRowsWithAdvanceColumn = (rows, projects, advanceColumnIndex) => {
  const projectLookup = createProjectLookup(projects);
  const entries = [];
  const unmatchedProjectNames = new Map();
  const seenEntries = new Set();
  let currentProject = null;

  rows.forEach((row, index) => {
    const rowNumber = index + 1;
    const rawProjectName = getCellText(row[0]);
    const rawTaskName = getCellText(row[1]);

    if (!shouldIgnoreProjectCell(rawProjectName)) {
      const matchedProject = projectLookup.find(rawProjectName);
      if (matchedProject) {
        currentProject = matchedProject;
        return;
      }

      unmatchedProjectNames.set(normalizeText(rawProjectName), {
        name: rawProjectName,
        rowNumber,
      });
      currentProject = null;
      return;
    }

    if (!currentProject || shouldIgnoreTaskName(rawTaskName)) return;

    const percentage = parsePercentValue(row[advanceColumnIndex]);
    if (percentage === null) return;

    const taskKey = compactKey(rawTaskName);
    const entryKey = `${currentProject.uuid}|${taskKey}`;

    if (seenEntries.has(entryKey)) return;
    seenEntries.add(entryKey);

    entries.push({
      projectUuid: currentProject.uuid,
      projectName: currentProject.name,
      taskName: rawTaskName,
      percentage: roundToTwo(percentage),
      rowNumber,
      advanceColumnIndex,
    });
  });

  return {
    entries,
    unmatchedProjectNames: Array.from(unmatchedProjectNames.values()),
  };
};

const parseSheetEntries = (workbook, type, projects) => {
  const config = SHEET_CONFIG[type];
  const sheet = findSheet(workbook, config.sheetName);

  if (!sheet) {
    return {
      type,
      sheetName: config.sheetName,
      sheetFound: false,
      entries: [],
      unmatchedProjectNames: [],
      advanceColumnIndex: DEFAULT_ADVANCE_COLUMN_INDEX,
    };
  }

  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: false,
    defval: '',
  });

  const candidates = detectAdvanceColumns(rows);
  const parsedCandidates = candidates.map((advanceColumnIndex) => ({
    advanceColumnIndex,
    ...parseRowsWithAdvanceColumn(rows, projects, advanceColumnIndex),
  }));

  const selected =
    parsedCandidates.sort((a, b) => b.entries.length - a.entries.length)[0] ||
    parseRowsWithAdvanceColumn(rows, projects, DEFAULT_ADVANCE_COLUMN_INDEX);

  return {
    type,
    sheetName: config.sheetName,
    sheetFound: true,
    entries: selected.entries,
    unmatchedProjectNames: selected.unmatchedProjectNames,
    advanceColumnIndex: selected.advanceColumnIndex,
  };
};

const readWorkbook = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        resolve(XLSX.read(data, { type: 'array' }));
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

const createTaskLookup = (records = []) => {
  const lookup = new Map();

  records.forEach((record) => {
    if (!record?.uuid_project || !record?.uuid_task || !record?.task_name) return;

    if (!lookup.has(record.uuid_project)) {
      lookup.set(record.uuid_project, {
        exact: new Map(),
        compact: new Map(),
      });
    }

    const projectLookup = lookup.get(record.uuid_project);
    projectLookup.exact.set(normalizeText(record.task_name), record);
    projectLookup.compact.set(compactKey(record.task_name), record);
  });

  return {
    find: (projectUuid, taskName) => {
      const projectLookup = lookup.get(projectUuid);
      if (!projectLookup) return null;

      const exactTask = projectLookup.exact.get(normalizeText(taskName));
      if (exactTask) return exactTask;

      return projectLookup.compact.get(compactKey(taskName)) || null;
    },
  };
};

const createContextMaps = (projects = [], lots = [], allotments = []) => {
  const lotsById = new Map(lots.map((lot) => [lot.uuid, lot]));
  const allotmentsById = new Map(allotments.map((allotment) => [allotment.uuid, allotment]));

  return {
    getProjectContext: (project) => {
      const lot = lotsById.get(project?.uuid_lot);
      const allotment = lot ? allotmentsById.get(lot.uuid_allotment) : null;
      const block = getCellText(lot?.block_number);
      const lotNumber = getCellText(lot?.lot_number);

      return {
        lot,
        allotment,
        lotLabel: [block, lotNumber].filter(Boolean).join(' - ') || 'Sin lote',
        allotmentLabel: allotment?.name || 'Sin loteo',
      };
    },
  };
};

const buildAnalysis = ({
  projects,
  lots,
  allotments,
  progressTracking,
  qualityTracking,
  parsedProgress,
  parsedQuality,
  fileName,
}) => {
  const projectsById = new Map(projects.map((project) => [project.uuid, project]));
  const progressLookup = createTaskLookup(progressTracking);
  const qualityLookup = createTaskLookup(qualityTracking);
  const { getProjectContext } = createContextMaps(projects, lots, allotments);
  const loadedDate = getArgentinaDate();

  const groupedProjects = new Map();
  const unmatched = [];
  const unmatchedTaskKeys = new Set();
  let unchangedCount = 0;

  const ensureProjectAnalysis = (project) => {
    if (!groupedProjects.has(project.uuid)) {
      groupedProjects.set(project.uuid, {
        project,
        ...getProjectContext(project),
        progressChanges: [],
        qualityChanges: [],
      });
    }

    return groupedProjects.get(project.uuid);
  };

  const addUnmatchedTask = (type, entry, reason) => {
    const key = `${type}|${entry.projectUuid}|${compactKey(entry.taskName)}`;

    if (unmatchedTaskKeys.has(key)) return;
    unmatchedTaskKeys.add(key);

    unmatched.push({
      type,
      projectUuid: entry.projectUuid,
      projectName: entry.projectName,
      taskName: entry.taskName,
      percentage: entry.percentage,
      rowNumber: entry.rowNumber,
      reason,
    });
  };

  parsedProgress.entries.forEach((entry) => {
    const project = projectsById.get(entry.projectUuid);
    const tracking = progressLookup.find(entry.projectUuid, entry.taskName);

    if (!project || !tracking) {
      addUnmatchedTask('progress', entry, 'La tarea no existe en la planilla asignada');
      return;
    }

    const maxPercentage = Number(tracking.max_percentage) || 0;
    const currentAbsolute = Number(tracking.current_percentage) || 0;
    const currentAbsoluteRounded = roundToTwo(currentAbsolute);
    const currentRelative = maxPercentage > 0 ? (currentAbsolute / maxPercentage) * 100 : 0;
    const nextAbsolute = maxPercentage > 0 ? (entry.percentage / 100) * maxPercentage : 0;
    const nextAbsoluteRounded = roundToTwo(nextAbsolute);

    if (
      Math.abs(currentAbsoluteRounded - nextAbsoluteRounded) <= PROGRESS_ABSOLUTE_TOLERANCE
    ) {
      unchangedCount += 1;
      return;
    }

    ensureProjectAnalysis(project).progressChanges.push({
      type: 'progress',
      taskName: tracking.task_name || entry.taskName,
      from: roundToTwo(currentRelative),
      to: roundToTwo(entry.percentage),
      rowNumber: entry.rowNumber,
      payload: {
        uuid_task: tracking.uuid_task,
        current_percentage: nextAbsoluteRounded,
        last_update_date: loadedDate,
      },
    });
  });

  parsedQuality.entries.forEach((entry) => {
    const project = projectsById.get(entry.projectUuid);
    const tracking = qualityLookup.find(entry.projectUuid, entry.taskName);

    if (!project || !tracking) {
      addUnmatchedTask('quality', entry, 'La tarea no existe en la planilla asignada');
      return;
    }

    const currentPercentage = Number(tracking.percentage) || 0;

    if (Math.abs(currentPercentage - entry.percentage) <= CHANGE_TOLERANCE) {
      unchangedCount += 1;
      return;
    }

    ensureProjectAnalysis(project).qualityChanges.push({
      type: 'quality',
      taskName: tracking.task_name || entry.taskName,
      from: roundToTwo(currentPercentage),
      to: roundToTwo(entry.percentage),
      rowNumber: entry.rowNumber,
      keepsFailed: tracking.status === 'failed',
      payload: {
        uuid_task: tracking.uuid_task,
        percentage: roundToTwo(entry.percentage),
        control_date: loadedDate,
      },
    });
  });

  const unmatchedProjects = [
    ...parsedProgress.unmatchedProjectNames.map((item) => ({
      ...item,
      type: 'progress',
      reason: 'Proyecto no encontrado',
    })),
    ...parsedQuality.unmatchedProjectNames.map((item) => ({
      ...item,
      type: 'quality',
      reason: 'Proyecto no encontrado',
    })),
  ];

  const projectAnalyses = Array.from(groupedProjects.values()).sort((a, b) =>
    (a.project?.name || '').localeCompare(b.project?.name || '', 'es-AR', { numeric: true })
  );

  const totalProgressChanges = projectAnalyses.reduce(
    (total, project) => total + project.progressChanges.length,
    0
  );
  const totalQualityChanges = projectAnalyses.reduce(
    (total, project) => total + project.qualityChanges.length,
    0
  );

  return {
    fileName,
    loadedDate,
    projects: projectAnalyses,
    unmatched,
    unmatchedProjects,
    unchangedCount,
    sheets: {
      progress: parsedProgress,
      quality: parsedQuality,
    },
    summary: {
      totalProjects: projectAnalyses.length,
      totalProgressChanges,
      totalQualityChanges,
      totalChanges: totalProgressChanges + totalQualityChanges,
      totalObservations: unmatchedProjects.length,
      totalIgnoredTasks: unmatched.length,
      totalReadRows: parsedProgress.entries.length + parsedQuality.entries.length,
    },
  };
};

const MetricTile = ({ label, value, tone = 'primary' }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      minHeight: 86,
      bgcolor: tone === 'warning' ? '#fff8e1' : tone === 'success' ? '#f1f8e9' : '#f7faf7',
    }}
  >
    <Typography variant="caption" color="text.secondary" fontWeight={700}>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={800} color={`${tone}.main`}>
      {value}
    </Typography>
  </Paper>
);

const ChangeRow = ({ change, type }) => {
  const Icon = type === 'progress' ? TimelineIcon : VerifiedUserIcon;
  const color = type === 'progress' ? 'primary.main' : 'success.main';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '28px minmax(180px, 1fr) 150px 90px' },
        gap: { xs: 0.5, md: 1.5 },
        alignItems: 'center',
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Icon sx={{ color, display: { xs: 'none', md: 'block' } }} fontSize="small" />
      <Typography variant="body2" fontWeight={700}>
        {change.taskName}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={toDisplayPercent(change.from)} size="small" variant="outlined" />
        <Typography variant="body2" color="text.secondary">
          →
        </Typography>
        <Chip
          label={toDisplayPercent(change.to)}
          size="small"
          color={type === 'progress' ? 'primary' : 'success'}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        Fila {change.rowNumber}
      </Typography>
      {change.keepsFailed && (
        <Typography
          variant="caption"
          color="error.main"
          sx={{ gridColumn: { xs: '1', md: '2 / -1' }, fontWeight: 700 }}
        >
          Marcado como fallido: se conserva ese estado manual.
        </Typography>
      )}
    </Box>
  );
};

const ProjectChangeCard = ({ projectAnalysis, expanded, onToggle }) => {
  const totalChanges =
    projectAnalysis.progressChanges.length + projectAnalysis.qualityChanges.length;

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          bgcolor: '#f7faf7',
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>
            {projectAnalysis.project?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {projectAnalysis.allotmentLabel} · {projectAnalysis.lotLabel}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label={`${totalChanges} cambios`} color="primary" size="small" />
          <Chip
            label={`${projectAnalysis.progressChanges.length} avance`}
            variant="outlined"
            size="small"
          />
          <Chip
            label={`${projectAnalysis.qualityChanges.length} calidad`}
            variant="outlined"
            size="small"
          />
          <IconButton size="small" onClick={onToggle}>
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 160ms ease',
              }}
            />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: 2 }}>
          {projectAnalysis.progressChanges.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="primary.main" fontWeight={800}>
                Progreso de obra
              </Typography>
              {projectAnalysis.progressChanges.map((change) => (
                <ChangeRow key={`progress-${change.payload.uuid_task}`} change={change} type="progress" />
              ))}
            </Box>
          )}

          {projectAnalysis.qualityChanges.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="success.main" fontWeight={800}>
                Control de calidad
              </Typography>
              {projectAnalysis.qualityChanges.map((change) => (
                <ChangeRow key={`quality-${change.payload.uuid_task}`} change={change} type="quality" />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const DashboardProgressImportDialog = ({
  open,
  onClose,
  projects = [],
  lots = [],
  allotments = [],
}) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [filters, setFilters] = useState({ uuid_allotment: '', uuid_lot: '' });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const bulkUpdateProgressMutation = useBulkUpdateProgress();
  const bulkUpdateQualityMutation = useBulkUpdateQuality();

  const lotsByAllotment = useMemo(() => {
    return lots.filter(
      (lot) => !filters.uuid_allotment || lot.uuid_allotment === filters.uuid_allotment
    );
  }, [filters.uuid_allotment, lots]);

  const lotOptions = lotsByAllotment.map((lot) => {
    const block = getCellText(lot.block_number);
    const lotNumber = getCellText(lot.lot_number);
    return {
      value: lot.uuid,
      label: [block, lotNumber].filter(Boolean).join(' - ') || 'Lote sin número',
    };
  });

  const allotmentOptions = allotments.map((allotment) => ({
    value: allotment.uuid,
    label: allotment.name || 'Loteo sin nombre',
  }));

  const filteredProjects = useMemo(() => {
    if (!analysis) return [];

    return analysis.projects.filter((projectAnalysis) => {
      if (
        filters.uuid_allotment &&
        projectAnalysis.lot?.uuid_allotment !== filters.uuid_allotment
      ) {
        return false;
      }

      if (filters.uuid_lot && projectAnalysis.project?.uuid_lot !== filters.uuid_lot) {
        return false;
      }

      return true;
    });
  }, [analysis, filters]);

  const filteredChangeCount = filteredProjects.reduce(
    (total, projectAnalysis) =>
      total + projectAnalysis.progressChanges.length + projectAnalysis.qualityChanges.length,
    0
  );

  const resetAnalysis = () => {
    setAnalysis(null);
    setExpandedProjects({});
    setError('');
    setSuccessMessage('');
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    resetAnalysis();
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setError('Seleccioná un archivo .xlsx antes de analizar.');
      return;
    }

    setIsAnalyzing(true);
    setError('');
    setSuccessMessage('');

    try {
      const [workbook, progressResponse, qualityResponse] = await Promise.all([
        readWorkbook(selectedFile),
        apiClient.get('project_progress_tracking/'),
        apiClient.get('project_quality_tracking/'),
      ]);

      const parsedProgress = parseSheetEntries(workbook, 'progress', projects);
      const parsedQuality = parseSheetEntries(workbook, 'quality', projects);

      if (!parsedProgress.sheetFound && !parsedQuality.sheetFound) {
        throw new Error('No se encontraron las hojas AVANCE FISICO ni AVANCE DE CALIDAD.');
      }

      const nextAnalysis = buildAnalysis({
        projects,
        lots,
        allotments,
        progressTracking: progressResponse.data || [],
        qualityTracking: qualityResponse.data || [],
        parsedProgress,
        parsedQuality,
        fileName: selectedFile.name,
      });

      const initialExpanded = nextAnalysis.projects.reduce((acc, projectAnalysis) => {
        acc[projectAnalysis.project.uuid] = true;
        return acc;
      }, {});

      setAnalysis(nextAnalysis);
      setExpandedProjects(initialExpanded);
    } catch (analysisError) {
      console.error('Error al analizar avance desde Excel:', analysisError);
      setError(analysisError.message || 'No se pudo analizar el archivo.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApply = async () => {
    if (!analysis?.summary?.totalChanges) return;

    setIsApplying(true);
    setError('');
    setSuccessMessage('');

    try {
      for (const projectAnalysis of analysis.projects) {
        if (projectAnalysis.progressChanges.length > 0) {
          await bulkUpdateProgressMutation.mutateAsync({
            projectId: projectAnalysis.project.uuid,
            progressUpdates: projectAnalysis.progressChanges.map((change) => change.payload),
          });
        }

        if (projectAnalysis.qualityChanges.length > 0) {
          await bulkUpdateQualityMutation.mutateAsync({
            projectId: projectAnalysis.project.uuid,
            qualityUpdates: projectAnalysis.qualityChanges.map((change) => change.payload),
          });
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projectProgressTracking'] }),
        queryClient.invalidateQueries({ queryKey: ['projectQualityTracking'] }),
        queryClient.invalidateQueries({ queryKey: ['projectProgressTrackingHistory'] }),
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
      ]);

      setSuccessMessage(`Se aplicaron ${analysis.summary.totalChanges} cambios correctamente.`);
      setAnalysis((prev) => (prev ? { ...prev, applied: true } : prev));
    } catch (applyError) {
      console.error('Error al aplicar avance desde Excel:', applyError);
      setError('No se pudieron aplicar los cambios analizados.');
    } finally {
      setIsApplying(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'uuid_allotment' ? { uuid_lot: '' } : {}),
    }));
  };

  const handleClose = () => {
    if (isAnalyzing || isApplying) return;
    setSelectedFile(null);
    setAnalysis(null);
    setExpandedProjects({});
    setFilters({ uuid_allotment: '', uuid_lot: '' });
    setError('');
    setSuccessMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const toggleProject = (projectUuid) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectUuid]: !prev[projectUuid],
    }));
  };

  return (
    <DialogContainer open={open} onClose={handleClose} size="large" minHeight="86vh">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <FactCheckIcon color="primary" />
          <Typography variant="h5" fontWeight={800}>
            Carga de avance desde Excel
          </Typography>
        </Box>
      </DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          hidden
          onChange={handleFileChange}
        />

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px dashed',
            borderColor: selectedFile ? 'primary.main' : 'divider',
            borderRadius: 1,
            bgcolor: selectedFile ? '#f7faf7' : 'background.paper',
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <CloudUploadIcon color="primary" sx={{ fontSize: 34 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={800} noWrap>
                {selectedFile?.name || 'Libro para carga de Dashboard.xlsx'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Hojas esperadas: AVANCE FISICO y AVANCE DE CALIDAD
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CustomButton variant="outlined" onClick={() => fileInputRef.current?.click()}>
              Seleccionar archivo
            </CustomButton>
            <CustomButton
              buttonIcon={FactCheckIcon}
              variant="base"
              onClick={handleAnalyze}
              disabled={!selectedFile || isAnalyzing || isApplying}
              isLoading={isAnalyzing}
            >
              Analizar avance
            </CustomButton>
          </Box>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}
        {successMessage && <Alert severity="success">{successMessage}</Alert>}

        {analysis && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricTile label="Proyectos con cambios" value={analysis.summary.totalProjects} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricTile
                  label="Cambios de avance"
                  value={analysis.summary.totalProgressChanges}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricTile
                  label="Cambios de calidad"
                  value={analysis.summary.totalQualityChanges}
                  tone="success"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricTile
                  label="Observaciones"
                  value={analysis.summary.totalObservations}
                  tone={analysis.summary.totalObservations > 0 ? 'warning' : 'success'}
                />
              </Grid>
            </Grid>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'stretch', md: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                  flexDirection: { xs: 'column', md: 'row' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterListIcon color="primary" />
                  <Typography variant="subtitle2" fontWeight={800}>
                    Filtros de revisión
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ minWidth: { md: 560 } }}>
                  <CustomAutoComplete
                    label="Loteo"
                    name="uuid_allotment"
                    value={filters.uuid_allotment}
                    onChange={handleFilterChange}
                    options={allotmentOptions}
                    optionValueKey="value"
                    optionLabelKey="label"
                    placeholder="Todos"
                    size="small"
                    sx={{ mt: 0 }}
                  />
                  <CustomAutoComplete
                    label="Lote"
                    name="uuid_lot"
                    value={filters.uuid_lot}
                    onChange={handleFilterChange}
                    options={lotOptions}
                    optionValueKey="value"
                    optionLabelKey="label"
                    placeholder="Todos"
                    size="small"
                    sx={{ mt: 0 }}
                  />
                </Stack>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Archivo: ${analysis.fileName}`} size="small" />
                <Chip label={`Fecha de carga: ${analysis.loadedDate}`} size="small" />
                <Chip
                  label={`Columna avance físico: ${String.fromCharCode(65 + analysis.sheets.progress.advanceColumnIndex)}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Columna calidad: ${String.fromCharCode(65 + analysis.sheets.quality.advanceColumnIndex)}`}
                  size="small"
                  variant="outlined"
                />
                <Chip label={`${analysis.unchangedCount} sin cambios`} size="small" variant="outlined" />
                {analysis.summary.totalIgnoredTasks > 0 && (
                  <Chip
                    label={`${analysis.summary.totalIgnoredTasks} tareas sin coincidencia`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
            </Paper>

            {isApplying && <LinearProgress />}

            {filteredProjects.length === 0 ? (
              <Alert severity="info">
                No hay cambios para los filtros seleccionados.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {filteredProjects.map((projectAnalysis) => (
                  <ProjectChangeCard
                    key={projectAnalysis.project.uuid}
                    projectAnalysis={projectAnalysis}
                    expanded={expandedProjects[projectAnalysis.project.uuid] !== false}
                    onToggle={() => toggleProject(projectAnalysis.project.uuid)}
                  />
                ))}
              </Stack>
            )}

            {analysis.unmatchedProjects.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'warning.light',
                  bgcolor: '#fff8e1',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="subtitle2" fontWeight={800}>
                    Observaciones del análisis
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {analysis.unmatchedProjects.map((item) => (
                    <Typography key={`${item.type}-${item.rowNumber}-${item.name}`} variant="body2">
                      Fila {item.rowNumber}: {item.name} · {item.reason}
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            )}

            {analysis.unmatched.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'warning.light',
                  bgcolor: '#fff8e1',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="subtitle2" fontWeight={800}>
                    Tareas sin coincidencia
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Estas filas se ignoran porque la tarea del Excel no existe en la planilla asignada
                  al proyecto. No se aplican como cambios.
                </Typography>
                <Stack spacing={1} sx={{ maxHeight: 260, overflow: 'auto', pr: 1 }}>
                  {analysis.unmatched.map((item) => (
                    <Typography
                      key={`${item.type}-${item.projectUuid}-${item.rowNumber}-${item.taskName}`}
                      variant="body2"
                      sx={{ lineHeight: 1.5 }}
                    >
                      <Box component="span" sx={{ fontWeight: 700, color: 'darkorange' }}>
                        {item.projectName}
                      </Box>{' '}
                      ·{' '}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 800,
                          color: item.type === 'progress' ? 'primary.main' : 'info.main',
                          textTransform: 'uppercase',
                        }}
                      >
                        {item.type === 'progress' ? 'PLANILLA DE PROGRESO' : 'PLANILLA DE CALIDAD'}
                      </Box>{' '}
                      ·{' '}
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {item.taskName}
                      </Box>{' '}
                      ·{' '}
                      <Box component="span" sx={{ fontStyle: 'italic' }}>
                        {item.reason}
                      </Box>
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            )}
          </>
        )}
      </DialogBody>

      <DialogActions
        sx={{
          p: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {analysis
            ? `${filteredChangeCount} cambios visibles · ${analysis.summary.totalChanges} cambios analizados`
            : 'Seleccioná el Excel y analizá antes de guardar.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <CustomButton
            buttonIcon={SaveIcon}
            variant="base"
            onClick={handleApply}
            disabled={!analysis?.summary?.totalChanges || analysis?.applied || isAnalyzing || isApplying}
            isLoading={isApplying}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Aplicar cambios
          </CustomButton>
          <CustomButton
            variant="warning"
            onClick={handleClose}
            disabled={isAnalyzing || isApplying}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Cerrar
          </CustomButton>
        </Box>
      </DialogActions>
    </DialogContainer>
  );
};

export default DashboardProgressImportDialog;
